import { useEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { ANIMATION_REGISTRY } from '@/config/animationRegistry';

export interface TopTabItem {
  id: string;
  label: string;
  /** Optional spotlight target for post-login / page tours. */
  tourId?: string;
  /** Optional count after the label, e.g. a `CountBadge` (renders nothing at zero). */
  badge?: ReactNode;
  /** Optional icon shown before the label (decorative; the label names the tab). */
  icon?: ComponentType<{ className?: string }>;
}

export interface TopTabsProps {
  tabs: TopTabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** Increment after login — stagger reveal left → right. */
  waveGeneration?: number;
  onAppearComplete?: () => void;
}

/**
 * Header nav tabs: secondary labels, the active one in primary text over a beam underline. Gap from the header chrome.
 * Each tab can carry its icon before the label, and tabs sit a wide gap apart, so the labels never read as one line.
 */
export default function TopTabs({
  tabs,
  activeId,
  onChange,
  className,
  waveGeneration = 0,
  onAppearComplete,
}: TopTabsProps) {
  const appearWave = waveGeneration > 0 && tabs.length >= 4;
  const { staggerMs, postLoginDelayMs } = ANIMATION_REGISTRY.navTabsWave;
  const [revealedIndex, setRevealedIndex] = useState(() => tabs.length - 1);
  const playedGenRef = useRef(0);

  useEffect(() => {
    if (!appearWave) {
      // Guest nav (1 tab) — keep home visible; do NOT flash all tabs visible during login transition.
      if (tabs.length < 4) setRevealedIndex(tabs.length - 1);
      return;
    }
    if (playedGenRef.current === waveGeneration) return;
    playedGenRef.current = waveGeneration;

    setRevealedIndex(-1);
    const timers: number[] = [];

    timers.push(
      window.setTimeout(() => {
        for (let i = 0; i < tabs.length; i++) {
          timers.push(
            window.setTimeout(() => {
              setRevealedIndex(i);
              if (i === tabs.length - 1) onAppearComplete?.();
            }, i * staggerMs)
          );
        }
      }, postLoginDelayMs)
    );

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [appearWave, waveGeneration, tabs.length, staggerMs, postLoginDelayMs, onAppearComplete]);

  return (
    <nav
      className={cx('flex items-center gap-6 min-w-0', className)}
      role="tablist"
    >
      {tabs.map((tab, index) => {
        const active = tab.id === activeId;
        const isRevealed = index <= revealedIndex;
        const tabStyle: CSSProperties = {
          opacity: isRevealed ? 1 : 0,
          transform: isRevealed ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.82)',
          pointerEvents: isRevealed ? undefined : 'none',
        };

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-hidden={appearWave && !isRevealed ? true : undefined}
            data-tour-id={tab.tourId}
            onClick={() => onChange(tab.id)}
            className={cx(
              /* Match header chip height so label baselines sit with logo / actions */
              'relative inline-flex items-center justify-center gap-1.5 h-[var(--proto-hd-chip-h)] font-ui text-body whitespace-nowrap leading-none',
              focusRing,
              // The wave's transition is a class (not inline) so the player's "animations off" setting can stop it.
              appearWave
                ? 'transition-[opacity,transform] duration-380 ease-[cubic-bezier(0.22,1,0.36,1)]'
                : 'transition-colors duration-(--k-dur-fast)',
              active ? 'font-bold text-primary' : 'font-normal text-secondary hover:text-primary'
            )}
            style={tabStyle}
          >
            {tab.icon ? (
              <span aria-hidden="true" className="inline-flex shrink-0">
                <tab.icon className="size-4.5" />
              </span>
            ) : null}
            {tab.label}
            {tab.badge ? <span className="inline-flex">{tab.badge}</span> : null}
            <span
              aria-hidden
              className={cx(
                'absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-beam origin-left transition-[opacity,transform] duration-(--k-dur-base)',
                active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}
